/**
 * Display preferences for the portrait / mobile layout (q-portrait-hud-and-orientation-settings,
 * AD-817; design `docs/design/mobile-layout.md` §4): the Orientation setting and the
 * Mobile-controls toggle. Client-local, stored in localStorage beside the other `pxl.*`
 * settings (SystemPanels, a11yPrefs) — nothing rides the socket.
 *
 * This module is also the HOOK the touch-controls overlay (T3) reads: `getLayoutPrefs()` /
 * `subscribeLayoutPrefs()` (and `useLayoutPrefs()` in React) say whether the player wants the
 * on-screen controls. Default OFF everywhere, touch included: tap-to-move and tap-to-attack
 * (T1) already cover movement and attack, and the owner's goal is screen space for the game.
 *
 * Duck-typed storage ports so it runs under `node --test` with no DOM.
 */

import { parseOrientationPref } from './layoutMode.js';
import type { OrientationPref } from './layoutMode.js';

export interface LayoutPrefs {
  orientation: OrientationPref;
  /** Show the on-screen movement stick + touch ability bar (T3). Default false. */
  mobileControls: boolean;
}

export const LAYOUT_KEYS = {
  orientation: 'pxl.orientation',
  mobileControls: 'pxl.mobileControls',
} as const;

export const DEFAULT_LAYOUT_PREFS: LayoutPrefs = Object.freeze({
  orientation: 'auto',
  mobileControls: false,
});

/** Read the stored prefs; anything missing or malformed falls back to the default. */
export function readLayoutPrefs(storage: Pick<Storage, 'getItem'> | null | undefined): LayoutPrefs {
  if (!storage) return { ...DEFAULT_LAYOUT_PREFS };
  try {
    return {
      orientation: parseOrientationPref(storage.getItem(LAYOUT_KEYS.orientation)),
      mobileControls: storage.getItem(LAYOUT_KEYS.mobileControls) === '1',
    };
  } catch {
    return { ...DEFAULT_LAYOUT_PREFS };
  }
}

/**
 * True once the on-screen-controls setting has been CHOSEN either way (Settings, or the first-run
 * offer, `q-demo-touch-first-run-onboarding`) — a stored value of any kind. The default-off of a
 * player who was never asked is not a choice, and the first-run step offers the controls only then.
 */
export function mobileControlsChosen(
  storage: Pick<Storage, 'getItem'> | null | undefined,
): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(LAYOUT_KEYS.mobileControls) !== null;
  } catch {
    return false;
  }
}

/** Persist the prefs. Never throws (storage may be refused in private mode). */
export function writeLayoutPrefs(
  storage: Pick<Storage, 'setItem'> | null | undefined,
  prefs: LayoutPrefs,
): void {
  if (!storage) return;
  try {
    storage.setItem(LAYOUT_KEYS.orientation, prefs.orientation);
    storage.setItem(LAYOUT_KEYS.mobileControls, prefs.mobileControls ? '1' : '0');
  } catch {
    // Storage refused: the choice holds for this page only.
  }
}

// ---- the live value + subscribers (one per page) ----

type Listener = (prefs: LayoutPrefs) => void;

let current: LayoutPrefs | null = null;
const listeners = new Set<Listener>();

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The prefs in force (read from storage once, then kept in memory). */
export function getLayoutPrefs(): LayoutPrefs {
  if (current === null) current = readLayoutPrefs(browserStorage());
  return current;
}

/** Change one or both prefs: persists, then tells every subscriber. */
export function setLayoutPrefs(patch: Partial<LayoutPrefs>): LayoutPrefs {
  const next: LayoutPrefs = { ...getLayoutPrefs(), ...patch };
  next.orientation = parseOrientationPref(next.orientation);
  next.mobileControls = next.mobileControls === true;
  current = next;
  writeLayoutPrefs(browserStorage(), next);
  for (const l of [...listeners]) l(next);
  return next;
}

/** Subscribe to changes; returns the unsubscribe. */
export function subscribeLayoutPrefs(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: forget the cached value (and listeners) so a test starts clean. */
export function resetLayoutPrefsForTest(): void {
  current = null;
  listeners.clear();
}
